import { client } from '@api/rest/client';
import { Spinner } from '@components/common/Spinner';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type { WeeklyBriefDetailDTO } from '@conversed/rest/src/contracts/weekly-brief/weekly-brief.dto';
import {
  WeeklyBriefStatus,
  WeeklyBriefTrigger,
} from '@conversed/rest/src/contracts/weekly-brief/weekly-brief.enums';
import { toastErrorTranslated, toastSuccessTranslated } from '@helpers/toast';
import { useModal } from '@hooks/modal/useModal';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WeeklyBriefStatusChip } from './WeeklyBriefStatusChip';
import { buildDetailLines } from './weeklyBrief.utils';

export type TWeeklyBriefModalProps = {
  briefId: string;
  onChanged: () => void;
};

type TPendingAction = 'approve' | 'reject';

export const WeeklyBriefModal: React.FC<TWeeklyBriefModalProps> = ({
  briefId,
  onChanged,
}) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const [brief, setBrief] = useState<WeeklyBriefDetailDTO | null>(null);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [pendingAction, setPendingAction] = useState<TPendingAction | null>(
    null,
  );
  const isSubmittingRef = useRef(false);
  // Approving after Monday 08:00 sends the emails before the request returns,
  // which takes a few seconds - the state shows that work is in progress.
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    client.weeklyBrief
      .getBrief({ params: { briefId } })
      .then(({ data }) => {
        if (!isCancelled) {
          setBrief(data);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setHasLoadFailed(true);
        }
        toastErrorTranslated(error);
      });

    return () => {
      isCancelled = true;
    };
  }, [briefId]);

  const confirmAction = async () => {
    // ButtonT only sets aria-disabled, so a second click still reaches here.
    if (!pendingAction || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (pendingAction === 'approve') {
        await client.weeklyBrief.approveBrief({
          params: { briefId },
          body: undefined,
        });
        toastSuccessTranslated('weekly-brief-settings.modal.approve-success');
      } else {
        await client.weeklyBrief.rejectBrief({
          params: { briefId },
          body: undefined,
        });
        toastSuccessTranslated('weekly-brief-settings.modal.reject-success');
      }
      onChanged();
      hideModal();
    } catch (error) {
      toastErrorTranslated(error);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      setPendingAction(null);
    }
  };

  if (hasLoadFailed) {
    return (
      <p className='text-sm text-red-base'>
        {t('weekly-brief-settings.modal.load-error')}
      </p>
    );
  }

  if (!brief) {
    return <Spinner size='md' />;
  }

  const isPendingApproval = brief.status === WeeklyBriefStatus.PendingApproval;
  const details = buildDetailLines(brief, t);

  // The modal panel caps its height (100dvh - 100px) but has no height of its
  // own. The email preview asks for 70vh and shrinks (down to 240px) only when
  // that does not fit under the cap, scrolling inside itself - so the modal uses
  // the free screen height and the buttons always stay on screen.
  return (
    <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto'>
      <div className='flex shrink-0 items-start justify-between gap-3'>
        <div>
          <p className='text-sm font-medium text-gray-800 mb-0'>
            {brief.weekLabel}
          </p>
          {brief.trigger === WeeklyBriefTrigger.Manual && (
            <p className='text-tiny text-gray-400 mb-0'>
              {t('weekly-brief-settings.history.manual')}
            </p>
          )}
        </div>
        <WeeklyBriefStatusChip status={brief.status} />
      </div>

      {brief.subject && (
        <div className='shrink-0'>
          <p className='text-pico uppercase tracking-wide text-gray-400 mb-1'>
            {t('weekly-brief-settings.modal.inbox-label')}
          </p>
          <div className='rounded-lg border border-gray-200 bg-white px-3 py-2'>
            <p className='text-tiny font-medium text-gray-800 mb-0'>
              {t('weekly-brief-settings.modal.sender')}
            </p>
            <p className='text-tiny font-semibold text-gray-800 mb-0'>
              {brief.subject}
            </p>
            {brief.preview && (
              <p className='text-tiny text-gray-400 mb-0 truncate'>
                {brief.preview}
              </p>
            )}
          </div>
        </div>
      )}

      {details.length > 0 && (
        <ul className='flex shrink-0 flex-col gap-0.5 text-tiny text-gray-500 mb-0'>
          {details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      )}

      {brief.html && (
        <iframe
          title={t('weekly-brief-settings.modal.email-preview')}
          sandbox=''
          srcDoc={brief.html}
          className='w-[640px] max-w-full basis-[70vh] shrink grow-0 min-h-[240px] border border-gray-200 rounded self-center'
        />
      )}

      {isPendingApproval && !pendingAction && (
        <div className='flex shrink-0 justify-end gap-2 pt-1'>
          <ButtonT
            size='xs'
            rounding='md'
            styling='outline-red'
            type='button'
            onClick={() => setPendingAction('reject')}>
            {t('weekly-brief-settings.modal.reject')}
          </ButtonT>
          <ButtonT
            size='xs'
            rounding='md'
            styling='primary'
            type='button'
            onClick={() => setPendingAction('approve')}>
            {t('weekly-brief-settings.modal.approve')}
          </ButtonT>
        </div>
      )}

      {isPendingApproval && pendingAction && (
        <div className='flex shrink-0 items-center justify-end gap-2 pt-1'>
          {isSubmitting ? (
            <span className='mr-auto flex items-center gap-2 text-tiny text-gray-500'>
              <Spinner size='sm' />
              {t(`weekly-brief-settings.modal.submitting-${pendingAction}`)}
            </span>
          ) : (
            <span className='text-tiny mr-auto'>
              {t(`weekly-brief-settings.modal.confirm-${pendingAction}`)}
            </span>
          )}
          <ButtonT
            size='xs'
            rounding='md'
            styling='cancel'
            type='button'
            disabled={isSubmitting}
            onClick={() => {
              if (!isSubmitting) {
                setPendingAction(null);
              }
            }}>
            {t('general.buttons.cancel')}
          </ButtonT>
          <ButtonT
            size='xs'
            rounding='md'
            styling={pendingAction === 'approve' ? 'primary' : 'danger'}
            type='button'
            disabled={isSubmitting}
            onClick={confirmAction}>
            {t('weekly-brief-settings.modal.confirm')}
          </ButtonT>
        </div>
      )}
    </div>
  );
};
